// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 (Agent E) — Billing ▸ Visits tab (asp-pot billing.html "Visit Records"): the agency and
// invoiced filters, the eleven-column table with the Invoiced / Ready / Unassigned badges, and the
// "Edit Visit" pop-up. Data: GET carerx/visits (agency_id / unbilled=1), which carries the nurse name,
// patient initials, MRN and drug on each row (owner 2026-10-29: they were read from the classic dashboard's
// newest 500 visits, so an older visit showed no nurse / MRN).
// Save = PUT carerx/visits/{id}/billing, the ASP PUT /api/billing/visits/:id: agency, visit type,
// city/state/ZIP (mig 453), mileage, after-hours, holiday — until the visit is on an invoice. The ASP
// free-text notes and street address are not stored (carerx_visits.notes is the clinical / void trail).
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { formatUsDate } from '../../../api/format'
import SearchableSelect from '../../../components/common/SearchableSelect'
import AppDialog from '../../../components/ui/AppDialog'
import { SelectField, type FieldOption } from '../../../components/ui/FormFields'
import { useToast } from '../../../components/ui/Toast'
import { PotCell, PotRow, PotTable } from '../potTable'
import { POT, potCard, potCardTitle } from '../potTheme'
import { updateCareRxVisitBilling } from '../../../api/client'
import { potBillingError, type PotAgency, type PotVisitRow } from '../potApi'
import { listPotBillingVisits } from './billingApi'
import { Badge, BILL, Empty, Fg, btnGhost, btnGhostSm, btnPrimary, fgInput, fgLabel, grid, helpText, hint, initialsOf, kitSelectSx } from './billingShared'
import { EMPTY_LOCATION, VisitLocationFields, YES_NO_OPTIONS, type VisitLocation } from './VisitBillingModal'

const STATUS_FILTER: FieldOption[] = [{ value: '', label: 'All visits' }, { value: 'uninvoiced', label: 'Uninvoiced only' }, { value: 'invoiced', label: 'Invoiced only' }]
const EDIT_TYPES: FieldOption[] = [
  { value: 'infusion', label: 'Infusion (standard)' },
  { value: 'admission', label: 'Admission / Start of Care' },
  { value: 'restart', label: 'Restart / Reauthorization' },
]
const COLS = [{ label: 'Date' }, { label: 'Nurse' }, { label: 'Patient' }, { label: 'Drug' }, { label: 'Hours', align: 'center' as const }, { label: 'Agency' }, { label: 'Type' }, { label: 'City' }, { label: 'Miles', align: 'center' as const }, { label: 'Status' }, { label: 'Actions' }]

/** A billing row = the OwlRx visit with its display fields. Owner 2026-10-29: nurse / initials / MRN come with
 *  the visit list itself — they were read from the classic dashboard's newest 500 visits, so older ones lost them. */
export interface BillingVisit extends PotVisitRow {
  nurse_name: string; patient_initials: string; patient_mrn: string | null; drug_name: string
}
export function mergeVisits(visits: PotVisitRow[]): BillingVisit[] {
  return visits.map(v => ({
    ...v,
    nurse_name: v.nurse_name ?? '',
    patient_initials: v.patient_initials || initialsOf(v.patient_name),
    patient_mrn: v.mrn ?? null,
    drug_name: v.therapy_drug ?? '',
  }))
}

export default function VisitsPanel({ agencies }: { agencies: PotAgency[] }) {
  const toast = useToast()
  const toastRef = useRef(toast); toastRef.current = toast
  const active = agencies.filter(a => a.status === 'active')
  const [agencyFilter, setAgencyFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [visits, setVisits] = useState<PotVisitRow[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [editing, setEditing] = useState<BillingVisit | null>(null)

  const load = useCallback(async () => {
    try {
      const serverAgency = agencyFilter && agencyFilter !== 'unassigned' ? agencyFilter : undefined
      const r = await listPotBillingVisits(serverAgency, statusFilter === 'uninvoiced')
      setVisits(r.items); setTruncated(r.truncated)
    } catch (e) { setVisits([]); toastRef.current.show('Visit load error: ' + potBillingError(e), 'error') }
  }, [agencyFilter, statusFilter])
  useEffect(() => { void load() }, [load])

  const rows = useMemo(() => {
    let list = mergeVisits(visits ?? [])
    if (statusFilter === 'invoiced') list = list.filter(v => v.billed)
    if (agencyFilter === 'unassigned') list = list.filter(v => !v.agency_id)
    return list
  }, [visits, statusFilter, agencyFilter])
  const agencyName = (id: string | null) => (id ? agencies.find(a => a.id === id)?.name ?? '' : '')

  return (
    <div style={potCard}>
      <h2 style={potCardTitle}>Visit Records <span style={{ fontSize: 12, fontWeight: 400, color: POT.text3 }}>(auto-created from completed POT assessments)</span></h2>
      <p style={{ ...helpText, marginBottom: 10 }}>Assign an agency, visit type, city, and mileage to each visit before generating an invoice. Only visits with an assigned agency can be invoiced.</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240 }}>
          <SearchableSelect ariaLabel="Filter visits by agency" value={agencyFilter} onChange={setAgencyFilter} placeholder="All (incl. unassigned)"
            options={[{ value: '', label: 'All (incl. unassigned)' }, { value: 'unassigned', label: 'Unassigned only' }, ...active.map(a => ({ value: a.id, label: a.name }))]} />
        </div>
        <SelectField size="small" label="" options={STATUS_FILTER} value={statusFilter} onChange={setStatusFilter} sx={{ ...kitSelectSx, minWidth: 160 }} inputProps={{ 'aria-label': 'Filter visits by invoice status' }} />
      </div>
      {truncated && <p style={{ ...helpText, color: BILL.warn }}>Showing the newest 1,000 visits — narrow the agency filter to see older ones.</p>}
      <PotTable cols={COLS} minWidth={1080} ariaLabel="Visit Records">
        {rows.map(v => {
          const invoiced = v.billed
          return (
            <PotRow key={v.id}>
              <PotCell style={{ fontSize: 12, whiteSpace: 'nowrap', opacity: invoiced ? .6 : 1 }}>{formatUsDate(v.visit_date)}</PotCell>
              <PotCell style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{v.nurse_name}</PotCell>
              <PotCell style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{v.patient_initials}{v.patient_mrn && <><br /><span style={{ color: POT.text3, fontSize: 10 }}>{v.patient_mrn}</span></>}</PotCell>
              <PotCell style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{v.drug_name}</PotCell>
              <PotCell align="center" style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{(Number(v.duration_hrs) || 0).toFixed(1)}</PotCell>
              <PotCell style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{v.agency_id ? agencyName(v.agency_id) : <span style={{ color: BILL.danger, fontSize: 11 }}>Not assigned</span>}</PotCell>
              <PotCell style={{ fontSize: 11, opacity: invoiced ? .6 : 1 }}>{v.visit_type || ''}</PotCell>
              <PotCell style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{v.city ? `${v.city}${v.state_code ? ', ' + v.state_code : ''}` : '—'}</PotCell>
              <PotCell align="center" style={{ fontSize: 12, opacity: invoiced ? .6 : 1 }}>{Number(v.mileage) || 0}</PotCell>
              <PotCell style={{ opacity: invoiced ? .6 : 1 }}>
                {invoiced ? <Badge kind="signed">Invoiced</Badge> : v.agency_id ? <Badge kind="draft">Ready</Badge> : <Badge kind="inactive">Unassigned</Badge>}
              </PotCell>
              <PotCell>{!invoiced && <button type="button" style={btnGhostSm} onClick={() => setEditing(v)}>Edit</button>}</PotCell>
            </PotRow>
          )
        })}
      </PotTable>
      {visits && rows.length === 0 && <Empty>No visits yet. Visits are created automatically when nurses complete POT assessments.</Empty>}
      {visits === null && <Empty>Loading…</Empty>}
      <EditVisitModal visit={editing} agencies={active} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load() }} />
    </div>
  )
}

/** asp-pot "Edit Visit". `visit` needs only the visit row; the nurse / patient / drug line shows what it has. */
export function EditVisitModal({ visit, agencies, onClose, onSaved }: {
  visit: (PotVisitRow & Partial<Pick<BillingVisit, 'nurse_name' | 'patient_initials' | 'drug_name'>>) | null
  agencies: PotAgency[]; onClose: () => void; onSaved: () => void
}) {
  const id = useId()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [agency, setAgency] = useState('')
  const [type, setType] = useState('infusion')
  const [loc, setLoc] = useState<VisitLocation>(EMPTY_LOCATION)
  const [mileage, setMileage] = useState('')
  const [afterHours, setAfterHours] = useState('false')
  const [holiday, setHoliday] = useState('false')
  useEffect(() => {
    if (!visit) return
    setAgency(visit.agency_id ?? ''); setType(visit.visit_type || 'infusion'); setErr(null)
    setLoc({ ...EMPTY_LOCATION, city: visit.city ?? '', state: visit.state_code ?? '', zip: visit.zip ?? '' })
    setMileage(visit.mileage ? String(visit.mileage) : ''); setAfterHours(visit.after_hours ? 'true' : 'false'); setHoliday(visit.holiday ? 'true' : 'false')
  }, [visit])
  if (!visit) return null
  const save = async () => {
    const miles = mileage.trim() === '' ? 0 : Number(mileage)
    if (!Number.isFinite(miles) || miles < 0) { setErr('Mileage must be a number of miles.'); return }
    setSaving(true); setErr(null)
    try {
      await updateCareRxVisitBilling(visit.id, {
        agency_id: agency, visit_type: type, city: loc.city.trim(), state_code: loc.state.trim(), zip: loc.zip.trim(),
        mileage: miles, after_hours: afterHours === 'true', holiday: holiday === 'true',
      })
      toast.show('Visit updated', 'success')
      onSaved()
    } catch (e) { setErr(potBillingError(e, 'Save failed')) } finally { setSaving(false) }
  }
  const fieldStyles = { input: fgInput, label: fgLabel, hint, selectSx: kitSelectSx }
  return (
    <AppDialog open onClose={onClose} title="Edit Visit" maxWidth="sm" busy={saving}
      actions={<>
        <button type="button" style={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" style={{ ...btnPrimary, opacity: saving ? .6 : 1 }} disabled={saving} onClick={() => { void save() }}>{saving ? 'Saving…' : 'Save Visit'}</button>
      </>}>
      {err && <p role="alert" style={{ color: BILL.danger, fontSize: 12, margin: '0 0 10px' }}>{err}</p>}
      <div style={{ background: POT.bg, padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 14, color: POT.text2 }}>
        <strong>{formatUsDate(visit.visit_date)}</strong> — {visit.nurse_name || '—'} — {visit.patient_initials || visit.patient_name || '??'} — {visit.drug_name || visit.therapy_drug || 'N/A'} — {(Number(visit.duration_hrs) || 0).toFixed(1)} hrs
      </div>
      <Fg label="Agency *">
        <SearchableSelect ariaLabel="Agency" value={agency} onChange={setAgency} placeholder="— Not assigned —"
          options={[{ value: '', label: '— Not assigned —' }, ...agencies.map(a => ({ value: a.id, label: a.name }))]} />
      </Fg>
      <Fg label="Visit Type" htmlFor={`${id}-type`}>
        <SelectField id={`${id}-type`} size="small" fullWidth label="" options={EDIT_TYPES} value={type} onChange={setType} sx={kitSelectSx} inputProps={{ 'aria-label': 'Visit Type' }} />
      </Fg>
      <VisitLocationFields value={loc} onChange={setLoc} styles={fieldStyles} />
      <div style={grid(2)}>
        <Fg label="Mileage (miles)" htmlFor={`${id}-miles`}><input id={`${id}-miles`} type="number" step="0.1" placeholder="0" style={fgInput} value={mileage} onChange={e => setMileage(e.target.value)} /></Fg>
      </div>
      <div style={grid(2)}>
        <Fg label="After-Hours" htmlFor={`${id}-ah`}>
          <SelectField id={`${id}-ah`} size="small" fullWidth label="" options={YES_NO_OPTIONS} value={afterHours} onChange={setAfterHours} sx={kitSelectSx} inputProps={{ 'aria-label': 'After-Hours' }} />
        </Fg>
        <Fg label="Holiday" htmlFor={`${id}-hol`}>
          <SelectField id={`${id}-hol`} size="small" fullWidth label="" options={YES_NO_OPTIONS} value={holiday} onChange={setHoliday} sx={kitSelectSx} inputProps={{ 'aria-label': 'Holiday' }} />
        </Fg>
      </div>
      <p style={{ fontSize: 11, color: POT.text3, margin: 0 }}>Agency, visit type, location, mileage, after-hours and holiday come from the POT submission; correct them here before the visit is invoiced.</p>
    </AppDialog>
  )
}
